import { useState, useMemo, useEffect } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
import searchIcon from '@iconify/icons-eva/search-fill';
import PropTypes from 'prop-types';
import {
  Box,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { CohortMatrixBadge } from 'components/_app/revenue-management/CohortMatrixBadge';
import { PATH_APP } from 'routes/paths';
import useAuth from 'hooks/useAuth';
import { buildChartNavGroups, isChartNavPathActive } from 'utils/chartNavGroups';
import { styled } from '@mui/material/styles';

/** Match NavSection ListItemStyle so Charts row aligns with other sidebar items. */
const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  minHeight: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(5),
  paddingRight: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.text.secondary,
}));

const ListItemIconStyle = styled(ListItemIcon)({
  minWidth: 20,
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
});

const chartMatchesSearch = (chart, groupTitle, query) => {
  const q = query.toLowerCase();
  return (
    chart.title.toLowerCase().includes(q) ||
    (chart.subtitle && chart.subtitle.toLowerCase().includes(q)) ||
    groupTitle.toLowerCase().includes(q) ||
    (chart.cohortView && (q.includes('matrix') || q.includes('cohort')))
  );
};

const ChartGroup = ({ group, isOpen, onToggle, searchQuery, pathname }) => {
  const theme = useTheme();

  // Filter charts based on search query
  const filteredCharts = useMemo(() => {
    if (!searchQuery) return group.charts;
    return group.charts.filter((chart) => chartMatchesSearch(chart, group.title, searchQuery));
  }, [group.charts, group.title, searchQuery]);

  // Don't show group if no charts match search
  if (filteredCharts.length === 0) return null;

  // Check if any chart in this group is active
  const isGroupActive = filteredCharts.some((chart) => isChartNavPathActive(pathname, chart.path));

  return (
    <Box>
      <ListItemButton
        onClick={onToggle}
        sx={{
          px: 2,
          py: 1,
          minHeight: 40,
          borderRadius: 1.5,
          mb: 0.5,
          ...(isGroupActive && {
            bgcolor: theme.palette.primary.main,
            color: 'common.white',
            '&:hover': {
              bgcolor: theme.palette.primary.main,
            },
          }),
          ...(!isGroupActive && {
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.5),
            },
          }),
        }}
      >
        <ListItemIcon sx={{ minWidth: 24, mr: 1 }}>
          <Icon
            icon={isOpen ? arrowIosDownwardFill : arrowIosForwardFill}
            width={16}
            height={16}
            style={{ color: isGroupActive ? theme.palette.common.white : theme.palette.text.secondary }}
          />
        </ListItemIcon>
        <ListItemIcon sx={{ minWidth: 20, mr: 1.5 }}>
          <Icon
            icon={group.icon}
            width={20}
            height={20}
            style={{ color: isGroupActive ? theme.palette.common.white : theme.palette.text.secondary }}
          />
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography
              variant='body2'
              sx={{
                fontSize: 13,
                fontWeight: isGroupActive ? 500 : 400,
                color: isGroupActive ? 'common.white' : 'text.primary',
              }}
            >
              {group.title}
            </Typography>
          }
        />
      </ListItemButton>

      <Collapse in={isOpen} timeout='auto' unmountOnExit>
        <List component='div' disablePadding sx={{ pl: 5.5, pt: 0.5 }}>
          {filteredCharts.map((chart) => (
            <NavLink
              key={chart.pathKey}
              to={chart.path}
              end
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              {({ isActive }) => (
                <ListItemButton
                  sx={{
                    px: 2,
                    py: 0.75,
                    minHeight: 36,
                    minWidth: 0,
                    overflow: 'hidden',
                    borderRadius: 1.5,
                    mb: 0.25,
                    ...(isActive && {
                      bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                      },
                    }),
                    ...(!isActive && {
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.5),
                      },
                    }),
                  }}
                >
                  <ListItemText
                    disableTypography
                    sx={{ m: 0, minWidth: 0 }}
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          minWidth: 0,
                          width: '100%',
                          flexWrap: 'nowrap',
                        }}
                      >
                        <Typography
                          variant='body2'
                          component='span'
                          title={chart.title}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 13,
                            fontWeight: isActive ? 500 : 400,
                            color: isActive ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {chart.title}
                        </Typography>
                        {chart.cohortView ? <CohortMatrixBadge variant='compact' /> : null}
                      </Box>
                    }
                  />
                </ListItemButton>
              )}
            </NavLink>
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

ChartGroup.propTypes = {
  group: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  pathname: PropTypes.string.isRequired,
};

ChartsNavItem.propTypes = {
  item: PropTypes.object.isRequired,
  isShow: PropTypes.bool.isRequired,
};

export default function ChartsNavItem({ item, isShow }) {
  const { appId } = useParams();
  const { pathname } = useLocation();
  const theme = useTheme();
  const { user } = useAuth();
  const organizationRole = user?.organizationRole;
  const { title, icon } = item;
  const chartsBasePath = `/applications/${appId}/charts`;
  const isActiveRoot = pathname.startsWith(chartsBasePath);

  const chartGroups = useMemo(() => buildChartNavGroups(appId, organizationRole), [appId, organizationRole]);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  // Keep "Charts" expanded when viewing any chart route (incl. full page refresh).
  useEffect(() => {
    if (isActiveRoot) {
      setOpen(true);
    }
  }, [isActiveRoot]);

  // Open default groups + always expand the group that contains the active chart.
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = {};
      chartGroups.forEach((group) => {
        const hasActiveChart = group.charts.some((chart) => isChartNavPathActive(pathname, chart.path));
        if (hasActiveChart) {
          next[group.id] = true;
        } else if (prev[group.id] !== undefined) {
          next[group.id] = prev[group.id];
        } else {
          next[group.id] = group.defaultOpen;
        }
      });
      return next;
    });
  }, [chartGroups, pathname]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleToggleGroup = (groupId) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Auto-expand groups when searching
  useEffect(() => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      setOpenGroups((prev) => {
        const expanded = { ...prev };
        chartGroups.forEach((group) => {
          const matchesGroup = group.title.toLowerCase().includes(query);
          const matchesAnyChart = group.charts.some((chart) => chartMatchesSearch(chart, group.title, searchQuery));

          if (matchesGroup || matchesAnyChart) {
            expanded[group.id] = true;
          }
        });
        return expanded;
      });
    }
  }, [searchQuery, chartGroups]);

  // Filter groups based on search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery) return chartGroups;

    const query = searchQuery.toLowerCase();
    return chartGroups.filter((group) => {
      const matchesGroup = group.title.toLowerCase().includes(query);
      const matchesAnyChart = group.charts.some((chart) => chartMatchesSearch(chart, group.title, searchQuery));
      return matchesGroup || matchesAnyChart;
    });
  }, [chartGroups, searchQuery]);

  const activeRootStyle = {
    bgcolor: theme.palette.primary.main,
    color: 'common.white',
    '&:hover': {
      bgcolor: theme.palette.primary.main,
    },
  };

  return (
    <>
      <ListItemStyle
        onClick={handleOpen}
        sx={{
          ...(isActiveRoot && activeRootStyle),
          ...(!isActiveRoot && {
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.5),
            },
          }),
        }}
      >
        <ListItemIconStyle>
          {icon && (
            <Box
              sx={{
                color: isActiveRoot ? 'common.white' : 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          )}
        </ListItemIconStyle>

        {isShow && (
          <>
            <ListItemText
              disableTypography
              primary={
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: 13,
                    fontWeight: isActiveRoot ? 500 : 400,
                    color: isActiveRoot ? 'common.white' : 'text.primary',
                  }}
                >
                  {title}
                </Typography>
              }
            />
            <Box
              component={Icon}
              icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
              sx={{
                width: 16,
                height: 16,
                ml: 1,
                flexShrink: 0,
                color: isActiveRoot ? 'common.white' : 'text.secondary',
              }}
            />
          </>
        )}
      </ListItemStyle>

      {isShow && (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            <TextField
              fullWidth
              size='small'
              placeholder='Search charts...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon icon={searchIcon} width={18} height={18} style={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.common.white, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.12),
                  },
                  '&.Mui-focused': {
                    bgcolor: alpha(theme.palette.common.white, 0.12),
                  },
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: 13,
                  py: 1,
                  color: theme.palette.text.secondary,
                  '&::placeholder': {
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                  },
                },
              }}
            />
            <NavLink
              to={PATH_APP.application.chart.explore(appId)}
              end
              style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
            >
              {({ isActive }) => (
                <ListItemButton
                  sx={{
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    borderRadius: 1.5,
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity)
                      : alpha(theme.palette.primary.main, 0.06),
                    color: isActive ? 'primary.main' : 'text.primary',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Icon icon='mdi:view-grid-outline' width={18} height={18} />
                  </ListItemIcon>
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography variant='body2' sx={{ fontSize: 13, fontWeight: 500 }}>
                        Browse all charts
                      </Typography>
                    }
                  />
                </ListItemButton>
              )}
            </NavLink>
            <Stack spacing={0.5}>
              {filteredGroups.map((group) => (
                <ChartGroup
                  key={group.id}
                  group={group}
                  isOpen={openGroups[group.id] || false}
                  onToggle={() => handleToggleGroup(group.id)}
                  searchQuery={searchQuery}
                  pathname={pathname}
                />
              ))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </>
  );
}
